import { useState } from 'react';
import { useIntl } from 'react-intl';

import { EntityPageFocusType } from '@xingternal/pages-graphql-types';

import { useGetCompanyInfoQuery } from './GetCompanyInfo.gql-types';
import type { GetCompanyInfoQuery } from './GetCompanyInfo.gql-types';
import { AppcuesCompanyData } from '../../typings/appcues';
import useXingFrame from '../useXingFrame';
import { usePageContext } from '../usePageContext';
import { useEditContext } from '../useEditContext';

const APPCUES_FOCUSTYPES: Array<String> = [EntityPageFocusType.Company];
export const APPCUES_FEATURESWITCH = 'use_appcues';

export type HookAppcuesData = {
  hasAppcues: boolean;
  user: Record<string, unknown>;
  appcuesComanyData: AppcuesCompanyData;
};

export const useQueryAppcuesData = (): HookAppcuesData => {
  const intl = useIntl();
  const { user, featureSwitches } = useXingFrame();
  const { pageContext } = usePageContext();
  const { isEditor } = useEditContext();

  const companyId = pageContext?.companyId;
  const focusType = pageContext?.focusType || '';

  const hasFeatureSwitch = Boolean(
    featureSwitches?.includes(APPCUES_FEATURESWITCH)
  );
  const hasFocusType = Boolean(APPCUES_FOCUSTYPES.includes(focusType));
  const hasRole = Boolean(isEditor);

  const hasAppcues = hasFeatureSwitch && hasFocusType && hasRole;

  const [appcuesComanyData, setAppcuesCompanyData] =
    useState<AppcuesCompanyData>(null);

  const onCompletedQuery = (data: GetCompanyInfoQuery) => {
    setAppcuesCompanyData({
      companyId: companyId ?? '',
      permissions: data?.company?.userContext?.userRole ?? '',
      companyName: data?.company?.companyName ?? '',
      contractType: data?.company?.contractPackageName ?? '',
      companySizeId: data?.company?.companySizeId ?? '',
      companySize: data?.company?.companySize ?? '',
      createdDate: data?.company?.createdAt ?? '',
      hasXamAccount: data?.company?.hasXamAccount ?? '',
      industry: data?.company?.industry?.localizationValue ?? '',
      industryId: data?.company?.industry?.id ?? '',
      language: intl?.locale ?? '',
      pageSlug: pageContext?.pageSlug ?? '',
    });
  };

  useGetCompanyInfoQuery({
    variables: {
      id: companyId,
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    skip: !hasAppcues || !companyId,
    onCompleted: onCompletedQuery,
  });

  return {
    hasAppcues,
    user,
    appcuesComanyData: hasAppcues ? appcuesComanyData : null,
  };
};

export default useQueryAppcuesData;
